import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileUp, Loader2, Sparkles, FileText, X, CheckCircle2, Image, Files, AlertCircle, Upload } from "lucide-react";
import { cn } from "@/lib/utils";
interface CreateApplicantFromDocumentsDialogProps {
  onSuccess: () => void;
}

interface ExtractedData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  address_street?: string;
  address_zip?: string;
  address_city?: string;
  birth_date?: string;
  desired_position?: string;
  qualifications?: string;
  drivers_license?: string;
  career_summary?: string;
}

interface DocumentPart {
  type: 'cover_letter' | 'cv' | 'certificate' | 'reference' | 'photo' | 'other';
  title: string;
  pageStart: number;
  pageEnd: number;
  confidence: number;
}

interface DetectedImage {
  name: string;
  mimeType: string;
  description?: string;
  hasContent: boolean;
  isProfilePhoto?: boolean;
}

interface ExtractedImageBase64 {
  name: string;
  base64: string;
  mimeType: string;
  description?: string;
  isProfilePhoto?: boolean;
}

interface UploadedFile {
  file: File;
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  cover_letter: 'Anschreiben',
  cv: 'Lebenslauf',
  certificate: 'Zeugnis',
  reference: 'Referenz',
  photo: 'Foto',
  other: 'Sonstiges',
};

const DOCUMENT_TYPE_COLORS: Record<string, string> = {
  cover_letter: 'bg-blue-500',
  cv: 'bg-green-500',
  certificate: 'bg-purple-500',
  reference: 'bg-orange-500',
  photo: 'bg-pink-500',
  other: 'bg-gray-500',
};

const CreateApplicantFromDocumentsDialog = ({ onSuccess }: CreateApplicantFromDocumentsDialogProps) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [editableData, setEditableData] = useState<ExtractedData>({});
  const [documentParts, setDocumentParts] = useState<DocumentPart[]>([]);
  const [detectedImages, setDetectedImages] = useState<DetectedImage[]>([]);
  const [extractedImagesBase64, setExtractedImagesBase64] = useState<ExtractedImageBase64[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [jobPositions, setJobPositions] = useState<{ id: string; title: string }[]>([]);

  useEffect(() => {
    if (!open) return;
    supabase
      .from("job_positions")
      .select("id, title")
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => setJobPositions(data || []));
  }, [open]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!analyzing) {
      setIsDragOver(true);
    }
  }, [analyzing]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (analyzing) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const newFiles: UploadedFile[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith("image/") || file.type === "application/pdf") {
          newFiles.push({ file });
        }
      }
      if (newFiles.length > 0) {
        setUploadedFiles(prev => [...prev, ...newFiles]);
        toast.success(`${newFiles.length} Datei(en) hinzugefügt`);
      } else {
        toast.error("Nur PDF- und Bilddateien werden unterstützt");
      }
    }
  }, [analyzing]);
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles: UploadedFile[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith("image/") || file.type === "application/pdf") {
        newFiles.push({ file });
      }
    }

    setUploadedFiles(prev => [...prev, ...newFiles]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const analyzeDocuments = async () => {
    if (uploadedFiles.length === 0) {
      toast.error("Bitte laden Sie mindestens ein Dokument hoch");
      return;
    }

    setAnalyzing(true);
    try {
      const formDataToSend = new FormData();
      uploadedFiles.forEach(({ file }) => {
        formDataToSend.append("files", file);
      });

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-applicant-documents`,
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429) {
          toast.error("Zu viele Anfragen. Bitte versuchen Sie es später erneut.");
        } else if (response.status === 402) {
          toast.error("Dienst momentan nicht verfügbar.");
        } else {
          throw new Error(errorData.error || "Fehler bei der Dokumentenanalyse");
        }
        return;
      }

      const result = await response.json();
      
      if (result.success) {
        // Set extracted applicant data
        if (result.data) {
          setExtractedData(result.data);
          setEditableData(result.data);
        } else {
          setExtractedData({});
          setEditableData({});
        }

        // Set document parts if detected
        if (result.documentParts && result.documentParts.length > 0) {
          setDocumentParts(result.documentParts);
          toast.success(`${result.documentParts.length} Dokumentteile erkannt!`);
        }

        // Set detected images
        if (result.detectedImages && result.detectedImages.length > 0) {
          setDetectedImages(result.detectedImages);
        }

        // Store extracted images (base64) for later upload
        if (result.extractedImagesBase64 && result.extractedImagesBase64.length > 0) {
          setExtractedImagesBase64(result.extractedImagesBase64);
          console.log(`Stored ${result.extractedImagesBase64.length} extracted images for upload`);
        }

        if (result.data && Object.keys(result.data).length > 0) {
          toast.success("Dokumente erfolgreich analysiert!");
        } else {
          toast.info("Keine Daten konnten extrahiert werden. Bitte füllen Sie die Felder manuell aus.");
        }
      } else {
        toast.info("Keine Daten konnten extrahiert werden. Bitte füllen Sie die Felder manuell aus.");
        setExtractedData({});
        setEditableData({});
      }
    } catch (error: any) {
      console.error("Error analyzing documents:", error);
      toast.error("Fehler bei der Dokumentenanalyse");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleInputChange = (field: keyof ExtractedData, value: string) => {
    setEditableData(prev => ({ ...prev, [field]: value }));
  };

  const createApplicant = async () => {
    if (!editableData.first_name || !editableData.last_name) {
      toast.error("Vorname und Nachname sind erforderlich");
      return;
    }
    if (!editableData.email) {
      toast.error("E-Mail ist erforderlich");
      return;
    }
    if (!editableData.phone) {
      toast.error("Telefonnummer ist erforderlich");
      return;
    }

    setSaving(true);
    try {
      // Create applicant
      const { data: applicant, error: insertError } = await supabase
        .from("applicants")
        .insert({
          first_name: editableData.first_name,
          last_name: editableData.last_name,
          email: editableData.email,
          phone: editableData.phone,
          address_street: editableData.address_street || null,
          address_zip: editableData.address_zip || null,
          address_city: editableData.address_city || null,
          birth_date: editableData.birth_date || null,
          desired_position: editableData.desired_position || null,
          qualifications: editableData.qualifications || null,
          drivers_license: editableData.drivers_license || null,
          career_summary: editableData.career_summary || null,
          current_stage: "new",
          source: "manual_upload",
          created_by: user?.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Save all uploaded files via AI function so they are guaranteed to be attached to the new applicant
      const formData = new FormData();
      uploadedFiles.forEach(({ file }) => {
        formData.append("files", file);
      });
      formData.append("applicantId", applicant.id);
      formData.append("userId", user?.id || "");
      formData.append("autoSave", "true");
      // Use splitting by default so multi-document PDFs get separated and categorized
      formData.append("splitDocuments", "true");

      const { data: functionData, error: functionError } = await supabase.functions.invoke(
        "parse-applicant-documents",
        { body: formData }
      );

      if (functionError) {
        console.error("parse-applicant-documents error:", functionError);
        throw functionError;
      }

      if (!functionData?.success) {
        console.error("parse-applicant-documents failed:", functionData);
        throw new Error(functionData?.error || "Dokumente konnten nicht gespeichert werden");
      }

      const savedCount = functionData.savedDocuments?.length || 0;
      const splitCount = functionData.splitResults?.successfulParts || 0;

      // Insert initial stage history
      await supabase.from("applicant_stage_history").insert({
        applicant_id: applicant.id,
        to_stage: "new",
        changed_by: user?.id,
        notes: `Bewerber aus Dokumenten-Upload erstellt${savedCount ? ` (${savedCount} Datei(en) gespeichert)` : ""}${splitCount ? `, ${splitCount} Teildokument(e) extrahiert` : ""}`,
      });

      toast.success("Bewerber erfolgreich angelegt!");
      setOpen(false);
      resetDialog();
      onSuccess();
    } catch (error: any) {
      console.error("Error creating applicant:", error);
      toast.error("Fehler beim Anlegen des Bewerbers");
    } finally {
      setSaving(false);
    }
  };

  const resetDialog = () => {
    setUploadedFiles([]);
    setExtractedData(null);
    setEditableData({});
    setDocumentParts([]);
    setDetectedImages([]);
    setExtractedImagesBase64([]);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) resetDialog();
    }}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <FileUp className="h-4 w-4 mr-2" />
          Aus Dokumenten
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Bewerber aus Dokumenten erstellen
          </DialogTitle>
          <DialogDescription>
            Laden Sie Bewerbungsunterlagen hoch. Bilder und Dokumente werden automatisch erkannt und extrahiert.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* File Upload Section with Drag & Drop */}
          <div className="space-y-4">
            <Label>Dokumente & Bilder hochladen</Label>
            
            {/* Drag & Drop Zone */}
            <div
              ref={dropzoneRef}
              onClick={() => !analyzing && fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                "relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all",
                isDragOver && !analyzing
                  ? "border-primary bg-primary/10 scale-[1.02]"
                  : "border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/50",
                analyzing && "opacity-50 cursor-not-allowed",
                uploadedFiles.length > 0 && "border-primary/50 bg-primary/5"
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                className="hidden"
                disabled={analyzing}
              />

              {uploadedFiles.length === 0 ? (
                <div className="space-y-3">
                  <Upload className={cn(
                    "h-12 w-12 mx-auto transition-colors",
                    isDragOver ? "text-primary" : "text-muted-foreground"
                  )} />
                  <div>
                    <p className="font-medium text-foreground">
                      Dateien hierher ziehen
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      oder klicken zum Auswählen
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    PDF, JPG, PNG, HEIC • Mehrere Dateien möglich
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <Files className="h-6 w-6" />
                    <span className="font-medium">{uploadedFiles.length} Datei(en) ausgewählt</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Weitere Dateien hinzufügen oder klicken zum Ersetzen
                  </p>
                </div>
              )}
            </div>

            {/* Selected Files List */}
            {uploadedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {uploadedFiles.map((uploadedFile, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg border group"
                  >
                    {uploadedFile.file.type.startsWith("image/") ? (
                      <Image className="h-4 w-4 text-pink-500" />
                    ) : (
                      <FileText className="h-4 w-4 text-blue-500" />
                    )}
                    <span className="text-sm truncate max-w-[150px]">{uploadedFile.file.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({(uploadedFile.file.size / 1024).toFixed(0)} KB)
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Analyze Button */}
            {uploadedFiles.length > 0 && !extractedData && (
              <Button
                type="button"
                onClick={analyzeDocuments}
                disabled={analyzing}
                className="w-full"
                size="lg"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Dokumente werden analysiert...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    {uploadedFiles.length} Dokument(e) mit KI analysieren
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Document Parts Detection Info */}
          {documentParts.length > 0 && (
            <div className="space-y-3 p-4 bg-muted/50 rounded-lg border">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Files className="h-4 w-4 text-primary" />
                Erkannte Dokumentteile
              </div>
              <div className="flex flex-wrap gap-2">
                {documentParts.map((part, index) => (
                  <Badge 
                    key={index} 
                    variant="secondary"
                    className={`${DOCUMENT_TYPE_COLORS[part.type] || 'bg-gray-500'} text-white`}
                  >
                    {DOCUMENT_TYPE_LABELS[part.type] || part.type}
                    {part.pageStart === part.pageEnd 
                      ? ` (S. ${part.pageStart})`
                      : ` (S. ${part.pageStart}-${part.pageEnd})`
                    }
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Die Dokumente werden nach Typ kategorisiert gespeichert.
              </p>
            </div>
          )}

          {/* Detected Images Info */}
          {detectedImages.length > 0 && (
            <div className="space-y-3 p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800">
              <div className="flex items-center gap-2 text-sm font-medium text-pink-700 dark:text-pink-300">
                <Image className="h-4 w-4" />
                Erkannte Bilder ({detectedImages.length})
              </div>
              <div className="space-y-1">
                {detectedImages.map((img, index) => (
                  <div key={index} className="text-xs text-muted-foreground flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                    {img.description || img.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Extracted Data Form */}
          {extractedData !== null && (
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                Daten extrahiert - bitte überprüfen und ergänzen
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Vorname *</Label>
                  <Input
                    id="first_name"
                    value={editableData.first_name || ""}
                    onChange={(e) => handleInputChange("first_name", e.target.value)}
                    placeholder="Vorname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nachname *</Label>
                  <Input
                    id="last_name"
                    value={editableData.last_name || ""}
                    onChange={(e) => handleInputChange("last_name", e.target.value)}
                    placeholder="Nachname"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={editableData.email || ""}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="E-Mail"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon *</Label>
                  <Input
                    id="phone"
                    value={editableData.phone || ""}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="Telefon"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_street">Straße</Label>
                <Input
                  id="address_street"
                  value={editableData.address_street || ""}
                  onChange={(e) => handleInputChange("address_street", e.target.value)}
                  placeholder="Straße und Hausnummer"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address_zip">PLZ</Label>
                  <Input
                    id="address_zip"
                    value={editableData.address_zip || ""}
                    onChange={(e) => handleInputChange("address_zip", e.target.value)}
                    placeholder="PLZ"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address_city">Stadt</Label>
                  <Input
                    id="address_city"
                    value={editableData.address_city || ""}
                    onChange={(e) => handleInputChange("address_city", e.target.value)}
                    placeholder="Stadt"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="birth_date">Geburtsdatum</Label>
                  <Input
                    id="birth_date"
                    type="date"
                    value={editableData.birth_date || ""}
                    onChange={(e) => handleInputChange("birth_date", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="drivers_license">Führerschein</Label>
                  <Input
                    id="drivers_license"
                    value={editableData.drivers_license || ""}
                    onChange={(e) => handleInputChange("drivers_license", e.target.value)}
                    placeholder="z.B. B, BE"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="desired_position">Gewünschte Stelle</Label>
                <Input
                  id="desired_position"
                  list="job-positions-list-create"
                  value={editableData.desired_position || ""}
                  onChange={(e) => handleInputChange("desired_position", e.target.value)}
                  placeholder="Position auswählen oder eingeben"
                />
                <datalist id="job-positions-list-create">
                  {jobPositions.map((p) => (
                    <option key={p.id} value={p.title} />
                  ))}
                </datalist>
              </div>

              <div className="space-y-2">
                <Label htmlFor="qualifications">Qualifikationen</Label>
                <Textarea
                  id="qualifications"
                  value={editableData.qualifications || ""}
                  onChange={(e) => handleInputChange("qualifications", e.target.value)}
                  placeholder="Qualifikationen und Erfahrung"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={createApplicant} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    "Bewerber anlegen"
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateApplicantFromDocumentsDialog;
